import { useCallback, useMemo, useState } from "react";
import { DropdownSelector, TextInput } from "src/components";
import { SelectorOption } from "src/components/DropdownSelector";
import { Applicant } from "src/types/Applicant";
import { WaCivilUnionAddendumValue } from "src/types/Application";

interface ConnectedToDropdownProps {
  onChange: (value: WaCivilUnionAddendumValue) => void;
  value?: WaCivilUnionAddendumValue;
  otherApplicants?: Applicant[];
  possessiveFirstName: string;
  hasConnectedToNameError?: boolean;
}

const NONE_OPTION: SelectorOption<string> = {
  // Use a UUID for the value to ensure it's unique
  value: "66265118-3507-4ac5-9147-1635ee18a9b4",
  label: "None of the above",
};

const ConnectedToDropdown = ({
  onChange,
  value,
  otherApplicants,
  possessiveFirstName,
  hasConnectedToNameError,
}: ConnectedToDropdownProps) => {
  const moreThanOneApplicant = !!otherApplicants?.length;
  const label = moreThanOneApplicant ? `${possessiveFirstName} Spouse` : "Your Spouse";

  const [dropdownValue, setDropdownValue] = useState<string>();
  const showTextInput = !moreThanOneApplicant || dropdownValue === NONE_OPTION.value;

  const onDropdownChange = useCallback(
    (newDropdownValue?: string) => {
      setDropdownValue(newDropdownValue);
      const connectedTo = otherApplicants?.find((applicant) => applicant?.email?.email === newDropdownValue);
      onChange({ ...value, connectedToName: connectedTo?.name });
    },
    [otherApplicants, onChange, value]
  );

  const onTextInputChange = useCallback(
    (newInputValue?: string) => {
      onChange({ ...value, connectedToName: newInputValue });
    },
    [value, onChange]
  );

  const options: SelectorOption<string>[] = useMemo(
    () =>
      otherApplicants?.length
        ? [
            ...otherApplicants.map((otherApplicant) => ({
              label: otherApplicant.name || otherApplicant.email?.email || "",
              value: otherApplicant?.email?.email || "",
            })),
            NONE_OPTION,
          ]
        : [],
    [otherApplicants]
  );

  return (
    <>
      {moreThanOneApplicant && (
        <DropdownSelector
          isClearable
          label={label}
          value={dropdownValue}
          error={!showTextInput && hasConnectedToNameError ? "Please select an option." : undefined}
          onChange={onDropdownChange}
          options={options}
        />
      )}
      {showTextInput && (
        <TextInput
          label={`${label} (full name)`}
          value={value?.connectedToName || ""}
          error={hasConnectedToNameError ? "Please enter both a first and last name." : undefined}
          placeholder="John Smith"
          onChange={onTextInputChange}
          autoComplete="name"
        />
      )}
    </>
  );
};

export default ConnectedToDropdown;
